import { useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import JobZImage from "../../../common/jobz-img";
import { useAuth } from "../../../../context/AuthContext";
import { jobService } from "../../../../services/job-service";
import { supportService } from "../../../../services/support-service";
import { canRoute, candidate, publicUser } from "../../../../globals/route-names";
import { NavLink } from "react-router-dom";
import { motion, MotionPanel, MotionRow, dashboardItem } from "../../../common/dashboard-motion";

function CanDashboardPage() {
    const { user } = useAuth();
    const [applications, setApplications] = useState([]);
    const [savedJobs, setSavedJobs] = useState([]);
    const [resumes, setResumes] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([jobService.myApplications(), jobService.mySavedJobs(), jobService.resumes(), supportService.myTickets()])
            .then(([applicationData, savedData, resumeData, ticketData]) => {
                setApplications(applicationData.applications || []);
                setSavedJobs(savedData.jobs || []);
                setResumes(resumeData.resumes || []);
                setTickets(ticketData.tickets || []);
            })
            .catch((error) => setMessage(error.message))
            .finally(() => setLoading(false));
    }, []);

    const completion = useMemo(() => {
        const fields = [
            user?.name, 
            user?.phone, 
            user?.headline, 
            user?.summary, 
            user?.skills, 
            user?.education, 
            resumes.length ? "file" : ""
        ];
        return Math.round((fields.filter(Boolean).length / fields.length) * 100);
    }, [user, resumes.length]);

    return (
        <MotionRow className="twm-right-section-panel site-bg-gray zz-dashboard-page zz-candidate-dashboard">
            {message && <div className="alert alert-info">{message}</div>}
            <motion.div className="wt-admin-right-page-header zz-dashboard-hero" variants={dashboardItem}>
                <div className="zz-dashboard-title-row">
                    <div>
                        <h2>Hello, {user?.name || "Candidate"}</h2>
                        <p>{loading ? "Loading your dashboard..." : "Track your applications, saved jobs, alerts, and resume readiness."}</p>
                    </div>
                    <div className="zz-action-strip">
                        <NavLink className="site-button zz-primary-action" to={publicUser.jobs.LIST}><i className="fa fa-search" /> Browse Jobs</NavLink>
                        <NavLink className="site-button-secondry" to={canRoute(candidate.APPLIED_JOBS)}><i className="fa fa-paper-plane" /> Applied Jobs</NavLink>
                        <NavLink className="site-button-secondry" to={canRoute(candidate.RESUME)}><i className="fa fa-file-alt" /> My Resume</NavLink>
                    </div>
                </div>
            </motion.div>
            <motion.div className="twm-dash-b-blocks mb-5" variants={dashboardItem}>
                <div className="row">
                    <StatCard className="gradi-1" icon="far fa-paper-plane" value={applications.length} label="Applications" />
                    <StatCard className="gradi-2" icon="far fa-bookmark" value={savedJobs.length} label="Saved Jobs" />
                    <StatCard className="gradi-3" icon="far fa-file-alt" value={resumes.length} label="Uploaded CVs" />
                    <StatCard className="gradi-4" icon="far fa-life-ring" value={tickets.filter((ticket) => !["resolved", "closed"].includes(ticket.status)).length} label="Open Support Tickets" />
                </div>
            </motion.div>
            <MotionPanel className="panel panel-default site-bg-white m-b30 border border-primary zz-support-panel">
                <div className="panel-body wt-panel-body p-a25 d-flex justify-content-between align-items-center flex-wrap">
                    <div>
                        <h4 className="m-b5"><i className="fa fa-headset text-primary" /> Need Admin Help?</h4>
                        <p className="m-b0">Raise support tickets for profile corrections, application questions, company/job concerns, or any Zozo Jobs query.</p>
                    </div>
                    <NavLink className="site-button" to={canRoute(candidate.SUPPORT)}>Raise Support Ticket</NavLink>
                </div>
            </MotionPanel>
            <div className="row">
                <div className="col-lg-12 col-md-12 mb-4">
                    <MotionPanel className="panel panel-default">
                        <div className="panel-heading wt-panel-heading p-a20">
                            <h4 className="panel-tittle m-a0">Profile Completion</h4>
                        </div>
                        <div className="panel-body wt-panel-body bg-white">
                            <div className="progress">
                                <div className="progress-bar site-bg-primary" role="progressbar" style={{ width: `${completion}%` }}>{completion}%</div>
                            </div>
                        </div>
                    </MotionPanel>
                </div>
                <div className="col-lg-12 col-md-12 mb-4">
                    <MotionPanel className="panel panel-default">
                        <div className="panel-heading wt-panel-heading p-a20">
                            <h4 className="panel-tittle m-a0">Recent Applications</h4>
                        </div>
                        <div className="panel-body wt-panel-body bg-white">
                            {applications.length === 0 ? (
                                <div className="zz-empty-state">
                                    <i className="fa fa-paper-plane" />
                                    <h4>No applications yet.</h4>
                                    <NavLink className="site-button" to={publicUser.jobs.LIST}>Browse Jobs and Apply</NavLink>
                                </div>
                            ) : applications.slice(0, 5).map((application, index) => (
                                <div className="twm-dash-candidates-list" key={application.id}>
                                    <div className="twm-media">
                                        <div className="twm-media-pic">
                                            <JobZImage src={`images/jobs-company/pic${(index % 5) + 1}.jpg`} alt="#" />
                                        </div>
                                    </div>
                                    <div className="twm-mid-content">
                                        <h4>{application.title}</h4>
                                        <p>{application.company_name || "Company"} - {application.location}</p>
                                        <div className="twm-jobs-category green"><span className="twm-bg-green">{application.status}</span></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </MotionPanel>
                </div>
                <div className="col-lg-12 col-md-12 mb-4">
                    <MotionPanel className="panel panel-default site-bg-white">
                        <div className="panel-heading wt-panel-heading p-a20">
                            <h4 className="panel-tittle m-a0">Recent Activity</h4>
                        </div>
                        <div className="panel-body wt-panel-body">
                            <div className="dashboard-list-box list-box-with-icon">
                                <ul>
                                    {applications.slice(0, 4).map((application) => (
                                        <li key={application.id}><i className="fa fa-paperclip text-success list-box-icon" />Applied for {application.title}.</li>
                                    ))}
                                    {savedJobs.slice(0, 4).map((job) => (
                                        <li key={job.id}><i className="fa fa-bookmark text-warning list-box-icon" />Saved {job.title}.</li>
                                    ))}
                                    {applications.length === 0 && savedJobs.length === 0 && (
                                        <li><i className="fa fa-info text-info list-box-icon" />Browse jobs and apply to start building activity. <NavLink to={publicUser.jobs.LIST}>Find jobs now</NavLink>.</li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </MotionPanel>
                </div>
            </div>
        </MotionRow>
    )
}

function StatCard({ className, icon, value, label }) {
    return (
        <div className="col-xl-3 col-lg-6 col-md-12 mb-3">
            <motion.div className="panel panel-default zz-stat-card" variants={dashboardItem} whileHover={{ y: -5, scale: 1.01 }}>
                <div className={`panel-body wt-panel-body ${className} dashboard-card`}>
                    <div className="wt-card-wrap">
                        <div className="wt-card-icon"><i className={icon} /></div>
                        <div className="wt-card-right counter"><CountUp end={value} duration={1} /></div>
                        <div className="wt-card-bottom"><h4 className="m-b0">{label}</h4></div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

export default CanDashboardPage;
